import { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useT } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function SiteNav() {
  const [open, setOpen] = useState(false);
  const [, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 22, mass: 0.4 });
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useT();

  const ROUTES = [
    { label: t("nav.works"), path: "/works", key: "works" },
    { label: t("nav.rooms"), path: "/exhibitions", key: "exhibitions" },
    { label: t("nav.about"), path: "/about", key: "about" },
    { label: t("nav.letter"), path: "/contact", key: "contact" },
    { label: t("nav.privacy"), path: "/privacy", key: "privacy" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Soft-navigate after the overlay closes; keep <a href> semantics intact.
  const handleNav = (e, path) => {
    // Allow modifier-clicks to behave natively (open in new tab/window).
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
    e.preventDefault();
    setOpen(false);
    setTimeout(() => {
      navigate(path);
      window.scrollTo(0, 0);
    }, 350);
  };

  const isCurrent = (path) => location.pathname === path;

  return (
    <>
      <motion.div
        style={{ scaleX: progress, transformOrigin: "0% 50%", background: "var(--accent-dynamic)" }}
        className="fixed top-0 left-0 right-0 h-[2px] z-[8100] transition-colors duration-700"
        data-testid="scroll-progress"
      />

      <header
        className="fixed top-0 left-0 right-0 z-[8000] transition-all duration-500 bg-bone/70 md:bg-bone/40 md:backdrop-blur-md border-b border-ink/10"
        style={{ WebkitBackdropFilter: "blur(8px) saturate(125%)", backdropFilter: "blur(8px) saturate(125%)" }}
      >
        <div className="flex items-center justify-between px-6 md:px-10 py-5">
          <Link
            to="/"
            data-testid="brand-home"
            onClick={(e) => handleNav(e, "/")}
            className="font-mono text-[11px] tracking-[0.3em] uppercase text-ink"
          >
            Rushit Shah
          </Link>

          <nav className="hidden md:flex items-center gap-8 font-mono text-[11px] tracking-[0.25em] uppercase text-ink" aria-label="Primary">
            {ROUTES.slice(0, 4).map((r) => (
              <Link
                key={r.path}
                to={r.path}
                data-testid={`nav-${r.key}`}
                onClick={(e) => handleNav(e, r.path)}
                aria-current={isCurrent(r.path) ? "page" : undefined}
                className={`link-underline ${isCurrent(r.path) ? "text-crimson" : ""}`}
              >
                {r.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-5 md:gap-6">
            <div className="hidden md:block">
              <LanguageSwitcher tone="ink" />
            </div>
            <button
              type="button"
              data-testid="menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="menu-overlay"
              className="font-mono text-[11px] tracking-[0.3em] uppercase link-underline text-ink"
            >
              {open ? t("nav.menu_close") : t("nav.menu_open")}
            </button>
          </div>
        </div>

      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="menu-overlay"
            data-testid="menu-overlay"
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.9, ease: [0.85, 0, 0.15, 1] }}
            className="fixed inset-0 z-[8500] bg-ink text-bone"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <div className="grain-overlay" />
            <div className="h-full w-full flex flex-col md:flex-row pt-24">
              <div className="flex-1 flex flex-col justify-center px-8 md:px-20 py-10">
                <div className="flex items-center justify-between mb-8 max-w-md">
                  <p className="font-mono text-[10px] tracking-[0.4em] uppercase opacity-60">
                    {t("nav.index_count", { count: ROUTES.length })}
                  </p>
                  <div className="md:hidden">
                    <LanguageSwitcher tone="bone" />
                  </div>
                </div>
                <ul className="space-y-1 md:space-y-2">
                  {ROUTES.map((r, idx) => (
                    <motion.li
                      key={r.path}
                      initial={{ y: 60, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.15 + idx * 0.06, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <Link
                        to={r.path}
                        data-testid={`overlay-nav-${r.key}`}
                        onClick={(e) => handleNav(e, r.path)}
                        className="font-serif text-4xl md:text-6xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tighter link-underline hover:text-crimson transition-colors"
                      >
                        {String(idx + 1).padStart(2, "0")}. {r.label}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </div>
              <div className="w-full md:w-[32%] border-t md:border-t-0 md:border-l border-bone/20 p-8 md:p-12 flex flex-col justify-between">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.4em] uppercase opacity-60 mb-6">{t("nav.studio")}</p>
                  <p className="font-serif text-2xl md:text-3xl leading-tight whitespace-pre-line">
                    {t("nav.studio_quote")}
                  </p>
                </div>
                <div className="space-y-2 font-mono text-[11px] uppercase tracking-[0.2em] opacity-80">
                  <a href="mailto:rs@rushitshah.com" data-testid="overlay-email" className="block link-underline">rs@rushitshah.com</a>
                  <a href="https://instagram.com/rushitshah08" target="_blank" rel="noreferrer" data-testid="overlay-instagram" className="block link-underline">Instagram ↗</a>
                  <a href="https://www.artworkarchive.com/profile/rushitshah" target="_blank" rel="noreferrer" data-testid="overlay-archive" className="block link-underline">Artwork Archive ↗</a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
